import {
  Box,
  List,
  ListItem,
  ListItemText,
  Stack,
  Typography
} from '@mui/material';
import { GridRowSelectionModel } from '@mui/x-data-grid';

import { useQueryClient } from '@tanstack/react-query';

import Button from '@/components/button';
import Dialog from '@/components/dialog/Dialog';
import { useDeleteCreator } from '@/hooks/queries/creators';
import useAlert from '@/hooks/useAlert';
import { Creator } from '@/models/creator';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  clearSelection: () => void;
  selected: GridRowSelectionModel;
  creatorsList: Array<Creator.Item>;
}

const DeleteCreators = ({
  isOpen,
  onClose,
  clearSelection,
  selected,
  creatorsList
}: Props) => {
  const queryClient = useQueryClient();
  const alert = useAlert();

  const selectedCreators = creatorsList.filter((creator) =>
    selected.includes(creator.id)
  );

  const affectedGroupIds = new Set<number>(
    selectedCreators.flatMap(({ groups }) => groups.map(({ id }) => id))
  );

  const { mutate: deleteCreator, isPending: isDeleting } = useDeleteCreator({
    onSuccess: async () => {
      alert.success(`Creator(s) has been successfully deleted`);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['creators'] }),
        queryClient.invalidateQueries({
          queryKey: ['creatorGroups'],
          refetchType: 'all'
        }),
        affectedGroupIds.forEach((id) => {
          queryClient.invalidateQueries({
            queryKey: ['creatorGroup', id],
            refetchType: 'all'
          });
        })
      ]);
      onClose();
      clearSelection();
    },
    onError: () => alert.error(`Some error occurred while deleting creators`)
  });

  const handleDeleteCreators = () => {
    deleteCreator({ ids: selected });
  };

  const count = selected.length;
  const label = count === 1 ? 'creator' : 'creators';

  return (
    <Dialog
      fullWidth
      title="Delete Creators"
      dialogContent={
        <Stack gap={1}>
          <Box>
            <Typography>
              {`Are you sure you want to delete ${count} ${label}?`}
            </Typography>
            <Typography color="error.main">
              You will not be able to recover these.
            </Typography>
          </Box>
          <Box sx={{ border: '1px solid gray', overflowY: 'auto' }}>
            <List dense disablePadding>
              {selectedCreators.map((creator) => (
                <ListItem
                  key={creator.id}
                  sx={{
                    px: 2,
                    py: 0.5,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    '&::before': {
                      content: '"•"',
                      color: 'text.secondary',
                      fontWeight: 'bold',
                      fontSize: '1.1rem',
                      lineHeight: 1
                    }
                  }}
                >
                  <ListItemText
                    primary={creator.name}
                    primaryTypographyProps={{ variant: 'body2' }}
                  />
                </ListItem>
              ))}
            </List>
          </Box>
        </Stack>
      }
      open={isOpen}
      onClose={onClose}
      actions={
        <>
          <Button onClick={onClose} variant="text" text="cancel" />
          <Button
            color="error"
            text="delete"
            onClick={handleDeleteCreators}
            loading={isDeleting}
          />
        </>
      }
    />
  );
};

export default DeleteCreators;
